import { Link } from "react-router-dom";
import { Users, Cpu, Shield, ShoppingCart, Zap, BarChart3, ArrowRight, ArrowUpRight } from "lucide-react";
import Spotlight from "../components/Spotlight";
import SectionTransition from "../components/SectionTransition";

const products = [
  {
    icon: Users,
    name: "Custom CRM Solutions",
    tagline: "Streamline Every Customer Interaction",
    desc: "Purpose-built customer relationship management systems designed to fit your unique workflow. Track leads, manage pipelines, and close deals faster with intelligent automation.",
    features: ["Lead & Pipeline Management", "Contact Analytics", "Automated Follow-ups", "Custom Reporting"],
    accent: "blue",
  },
  {
    icon: ShoppingCart,
    name: "E-Commerce Platforms",
    tagline: "Sell Smarter, Scale Faster",
    desc: "Full-featured e-commerce solutions with modern storefronts, secure payments, and powerful inventory management. Built to handle thousands of transactions seamlessly.",
    features: ["Multi-Vendor Support", "Payment Gateway Integration", "Inventory Automation", "Mobile-First Design"],
    accent: "emerald",
  },
  {
    icon: Cpu,
    name: "AI-Powered Analytics",
    tagline: "Insights That Drive Decisions",
    desc: "Transform raw data into actionable intelligence. Our AI analytics dashboards provide real-time metrics, predictive models, and automated reporting for smarter business decisions.",
    features: ["Predictive Analytics", "Real-Time Dashboards", "Natural Language Queries", "Automated Reports"],
    accent: "violet",
  },
  {
    icon: Shield,
    name: "Enterprise SaaS Applications",
    tagline: "Cloud-Native, Enterprise-Ready",
    desc: "Scalable software-as-a-service platforms built with microservices architecture. Multi-tenant, secure, and designed to grow with your user base from day one.",
    features: ["Multi-Tenant Architecture", "Role-Based Access", "API-First Design", "99.9% Uptime SLA"],
    accent: "amber",
  },
  {
    icon: Zap,
    name: "ERP Systems",
    tagline: "Unify Your Business Operations",
    desc: "Enterprise resource planning solutions that bring together finance, HR, supply chain, and operations into one cohesive platform. Eliminate silos and boost efficiency.",
    features: ["Financial Management", "HR & Payroll", "Supply Chain Tracking", "Process Automation"],
    accent: "rose",
  },
  {
    icon: BarChart3,
    name: "POS Systems",
    tagline: "Next-Generation Point of Sale",
    desc: "Modern point-of-sale systems that go beyond transactions. Manage inventory, track sales analytics, and deliver exceptional customer experiences at every touchpoint.",
    features: ["Multi-Location Support", "Real-Time Inventory", "Sales Analytics", "Customer Loyalty Programs"],
    accent: "cyan",
  },
];

const accentStyles = {
  blue: "bg-blue-50 text-blue-600 border-blue-100",
  emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
  violet: "bg-violet-50 text-violet-600 border-violet-100",
  amber: "bg-amber-50 text-amber-600 border-amber-100",
  rose: "bg-rose-50 text-rose-600 border-rose-100",
  cyan: "bg-cyan-50 text-cyan-600 border-cyan-100",
};

const accentDot = {
  blue: "bg-blue-500",
  emerald: "bg-emerald-500",
  violet: "bg-violet-500",
  amber: "bg-amber-500",
  rose: "bg-rose-500",
  cyan: "bg-cyan-500",
};

export default function ProductsPage() {
  return (
    <div data-testid="products-page">
      {/* Hero */}
      <Spotlight className="bg-neutral-950 grid-pattern pt-32 pb-20 md:pt-44 md:pb-28">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-medium tracking-wider uppercase text-blue-400 mb-4 animate-fade-in-up">Our Products</p>
            <h1
              data-testid="products-heading"
              className="text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-white mb-6 animate-fade-in-up animation-delay-200"
            >
              Products
            </h1>
            <p className="text-lg md:text-xl text-slate-400 leading-relaxed animate-fade-in-up animation-delay-400">
              Purpose-built software products designed for modern businesses. 
              Each solution is crafted with enterprise-grade reliability and cutting-edge technology.
            </p>
          </div>
        </div>
      </Spotlight>

      {/* Products Grid */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {products.map((product, i) => (
              <SectionTransition key={product.name} delay={i * 0.08}>
                <div
                  data-testid={`product-card-${i}`}
                  className="group relative p-8 md:p-10 rounded-2xl border border-slate-200 hover:border-slate-300 bg-white hover:shadow-xl transition-all duration-400"
                >
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 ${accentStyles[product.accent]}`}>
                    <product.icon size={24} strokeWidth={1.5} />
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-semibold text-slate-900 mb-2">{product.name}</h3>
                  <p className="text-sm font-medium text-slate-500 mb-4">{product.tagline}</p>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6">{product.desc}</p>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-2 mb-6">
                    {product.features.map((f) => (
                      <div key={f} className="flex items-center gap-2 text-xs text-slate-600">
                        <span className={`w-1.5 h-1.5 rounded-full ${accentDot[product.accent]}`} />
                        {f}
                      </div>
                    ))}
                  </div>

                  {/* Link */}
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 hover:text-blue-600 transition-colors group/link"
                  >
                    Learn More <ArrowUpRight size={14} className="group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
                  </Link>
                </div>
              </SectionTransition>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-50 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <SectionTransition>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-slate-900 mb-6">
              Need a Custom Product?
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-xl mx-auto mb-10">
              We build tailored solutions from scratch. Tell us your requirements and 
              we will bring your product vision to life.
            </p>
            <Link
              to="/contact"
              data-testid="products-cta-btn"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-slate-900 text-white font-semibold text-sm hover:bg-slate-800 transition-all duration-200 active:scale-95"
            >
              Request a Demo <ArrowRight size={16} />
            </Link>
          </SectionTransition>
        </div>
      </section>
    </div>
  );
}
